import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const STATIC_PASSWORD_REQUIREMENTS = [
  "At least 8 characters in length",
  "Contain at least 3 of the following 4 types of characters: lower case letters (a-z), upper case letters (A-Z), numbers (0-9), special characters (e.g. !@#$%^&*)",
];

const COMMON_EMAIL_DOMAIN_TYPOS = {
  "gmail.comm": "gmail.com",
  "gmail.con": "gmail.com",
  "gamil.com": "gmail.com",
  "gmial.com": "gmail.com",
  "yahoo.comm": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "hotmail.comm": "hotmail.com",
  "outlook.comm": "outlook.com",
  "icloud.comm": "icloud.com",
};

function suggestEmailCorrection(email) {
  const parts = (email || "").trim().split("@");
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  const correctedDomain = COMMON_EMAIL_DOMAIN_TYPOS[domain.toLowerCase()];
  if (!correctedDomain) return null;
  return `${local}@${correctedDomain}`;
}

const GOOGLE_LOGO_SVG = (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.23 0 2.35.42 3.23 1.25l2.37-2.37C17.46 2.39 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

export default function EmbeddedLoginForm({
  onLoginStart,
  onLoginSuccess,
  onLoginError,
  onCancel,
  onSocialRedirect,
  initialEmail = "",
}) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [inlineError, setInlineError] = useState("");

  useEffect(() => {
    let active = true;
    fetch("/api/auth/password-policy")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (
          active &&
          Array.isArray(data?.requirements) &&
          data.requirements.length > 0
        ) {
          setPasswordRequirements(data.requirements);
        }
      })
      .catch(() => null);
    return () => {
      active = false;
    };
  }, []);

  const reportError = (msg) => {
    setInlineError(msg || "");
    if (onLoginError) onLoginError(msg || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      reportError("Please enter your email and password.");
      return;
    }

    const suggestion = suggestEmailCorrection(normalizedEmail);
    if (suggestion && suggestion !== emailSuggestion) {
      setEmailSuggestion(suggestion);
      return;
    }

    if (mode === "signup" && password.length < 8) {
      reportError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    reportError("");
    if (onLoginStart) onLoginStart();

    try {
      const response = await fetch(
        mode === "signup"
          ? "/api/auth/embedded-signup"
          : "/api/auth/embedded-login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            mode === "signup"
              ? { email: normalizedEmail, password, name: name.trim() }
              : { email: normalizedEmail, password }
          ),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        const message =
          (data && data.error && data.error.message) ||
          (mode === "signup"
            ? "Signup failed. Please try again."
            : "Login failed. Please try again.");
        reportError(message);
        setLoading(false);
        return;
      }

      onLoginSuccess({
        ...data.user,
        accessToken: data.accessToken || null,
      });
    } catch {
      reportError("Network error. Please try again.");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      reportError("Please enter your email address.");
      return;
    }

    setLoading(true);
    reportError("");
    if (onLoginStart) onLoginStart();

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        reportError(
          (data && data.error && data.error.message) ||
            "Failed to send reset email. Please try again."
        );
        return;
      }
      setResetSent(true);
      reportError("");
    } catch {
      reportError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (nextMode) => {
    if (loading) return;
    setMode(nextMode);
    setPassword("");
    setForgotPassword(false);
    setResetSent(false);
    reportError("");
  };

  if (forgotPassword && mode === "login") {
    return (
      <Box
        sx={{
          border: "1px solid #cbd5e1",
          borderRadius: "12px",
          p: 2.5,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Reset your password
          </Typography>
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => {
              setForgotPassword(false);
              setResetSent(false);
              reportError("");
            }}
            sx={{ color: "text.secondary", textDecoration: "underline" }}
          >
            Back to login
          </Link>
        </Box>

        {inlineError ? (
          <Alert severity="error" sx={{ mb: 2, fontSize: "0.8rem" }}>
            {inlineError}
          </Alert>
        ) : null}

        {resetSent ? (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              If an account exists for <b>{email.trim()}</b>, you&apos;ll
              receive a reset link shortly.
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "text.secondary" }}
            >
              Check your inbox, reset your password, then return here to log in.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleForgotPassword}>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 2, color: "text.secondary" }}
            >
              Enter your email and we&apos;ll send a password reset link.
            </Typography>
            <TextField
              label="Email*"
              type="email"
              size="small"
              fullWidth
              required
              disabled={loading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ py: 1.1 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  Sending reset link...
                </>
              ) : (
                "Send reset link"
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  const isSignup = mode === "signup";
  const requirementsList =
    passwordRequirements && passwordRequirements.length > 0
      ? passwordRequirements
      : STATIC_PASSWORD_REQUIREMENTS;

  return (
    <Box
      sx={{
        border: "1px solid #cbd5e1",
        borderRadius: "12px",
        p: 2.5,
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {isSignup ? "Create account to continue" : "Log in to continue"}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={onCancel}
          sx={{ color: "text.secondary", textDecoration: "underline" }}
        >
          Cancel
        </Link>
      </Box>

      {inlineError ? (
        <Alert severity="error" sx={{ mb: 2, fontSize: "0.8rem" }}>
          {inlineError}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {!isSignup && onSocialRedirect ? (
            <>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                disabled={loading}
                onClick={() => onSocialRedirect("google-oauth2")}
                startIcon={GOOGLE_LOGO_SVG}
                sx={{ py: 1.1, textTransform: "none", fontWeight: 600 }}
              >
                Continue with Google
              </Button>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ height: 1, flex: 1, backgroundColor: "#e2e8f0" }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  or
                </Typography>
                <Box sx={{ height: 1, flex: 1, backgroundColor: "#e2e8f0" }} />
              </Stack>
            </>
          ) : null}
          {isSignup ? (
            <TextField
              label="Full Name"
              size="small"
              fullWidth
              disabled={loading}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : null}

          <TextField
            label="Email*"
            type="email"
            size="small"
            fullWidth
            required
            disabled={loading}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailSuggestion(null);
            }}
          />

          {emailSuggestion ? (
            <Typography variant="caption" sx={{ color: "warning.dark" }}>
              Did you mean{" "}
              <Link
                component="button"
                type="button"
                onClick={() => {
                  setEmail(emailSuggestion);
                  setEmailSuggestion(null);
                }}
                sx={{ fontWeight: 700 }}
              >
                {emailSuggestion}
              </Link>
              ?
            </Typography>
          ) : null}

          <TextField
            label="Password*"
            type="password"
            size="small"
            fullWidth
            required
            disabled={loading}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {isSignup ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                Password requirements:
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {requirementsList.map((req) => (
                  <Typography
                    key={req}
                    component="li"
                    variant="caption"
                    sx={{ color: "text.secondary", display: "list-item" }}
                  >
                    {req}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                disabled={loading}
                onClick={() => setForgotPassword(true)}
                sx={{ color: "text.secondary", textDecoration: "underline" }}
              >
                Forgot password?
              </Link>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{ py: 1.2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                {isSignup ? "Creating account..." : "Logging in..."}
              </>
            ) : isSignup ? (
              "Sign up & Checkout"
            ) : (
              "Log in & Checkout"
            )}
          </Button>

          <Typography
            variant="caption"
            sx={{ textAlign: "center", color: "text.secondary" }}
          >
            {isSignup ? (
              <>
                Already have an account?{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => switchMode("login")}
                  sx={{ fontWeight: 700, textDecoration: "underline" }}
                >
                  Log in
                </Link>
              </>
            ) : (
              <>
                Don&apos;t have an account?{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => switchMode("signup")}
                  sx={{ fontWeight: 700, textDecoration: "underline" }}
                >
                  Sign up
                </Link>
              </>
            )}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
